import { useMemo, useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Checkbox } from "./ui/checkbox";

// Generic sortable table.
// columns: [{ key, header, sortable, accessor(row), render(row), align, mono }]
export function DataTable({
  columns,
  data,
  getRowId = (r) => r.id,
  initialSort = null, // { key, dir }
  selectable = false,
  selectedIds = [],
  onToggleRow,
  onToggleAll,
  emptyText = "No records found.",
  testId = "data-table",
}) {
  const [sort, setSort] = useState(initialSort);

  const sorted = useMemo(() => {
    if (!sort) return data;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return data;
    const acc = col.accessor || ((r) => r[col.key]);
    const copy = [...data];
    copy.sort((a, b) => {
      let va = acc(a);
      let vb = acc(b);
      if (va == null) va = "";
      if (vb == null) vb = "";
      if (typeof va === "number" && typeof vb === "number") {
        return sort.dir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sort.dir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return copy;
  }, [data, sort, columns]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const allSelected = selectable && data.length > 0 && selectedIds.length === data.length;

  return (
    <div className="w-full overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow className="bg-slate-50/80 dark:bg-slate-800/60 hover:bg-slate-50/80 dark:hover:bg-slate-800/60">
            {selectable && (
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(v) => onToggleAll?.(!!v)}
                  aria-label="Select all rows"
                  data-testid={`${testId}-select-all`}
                />
              </TableHead>
            )}
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={`whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 ${
                  col.align === "right" ? "text-right" : ""
                }`}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    data-testid={`${testId}-sort-${col.key}`}
                    className={`inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-slate-100 transition-colors ${
                      col.align === "right" ? "flex-row-reverse" : ""
                    }`}
                  >
                    {col.header}
                    {!sort || sort.key !== col.key ? (
                      <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
                    ) : sort.dir === "asc" ? (
                      <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 text-blue-600" />
                    )}
                  </button>
                ) : (
                  col.header
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="h-32 text-center text-sm text-slate-500 dark:text-slate-400"
                data-testid={`${testId}-empty`}
              >
                {emptyText}
              </TableCell>
            </TableRow>
          ) : (
            sorted.map((row) => {
              const id = getRowId(row);
              const checked = selectedIds.includes(id);
              return (
                <TableRow
                  key={id}
                  data-testid={`${testId}-row-${id}`}
                  className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors"
                >
                  {selectable && (
                    <TableCell className="w-12">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => onToggleRow?.(id)}
                        aria-label="Select row"
                        data-testid={`${testId}-select-${id}`}
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={`${col.align === "right" ? "text-right" : ""} ${
                        col.mono ? "font-mono text-sm tracking-tight" : "text-sm"
                      } text-slate-700 dark:text-slate-200`}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
